// Enhanced Quiz History Component
// Replace the history section in StudentDashboard.js

import React, { useState, useEffect } from 'react';
import API_BASE_URL from './config';
import { API_BASE, authFetch } from '../services/api.js';
import './quizenhancedhistory.css';
import CustomSelect from '../components/CustomSelect';

const EnhancedQuizHistory = ({ studentId, onQuizSelect, specificQuizId = null }) => {
    //console.log('🔍 DEBUG: EnhancedQuizHistory component loaded!');
    // Filter states
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [topics, setTopics] = useState([]);
    const [subtopics, setSubtopics] = useState([]);
    
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedChapter, setSelectedChapter] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [selectedSubtopic, setSelectedSubtopic] = useState('');
    const [viewType, setViewType] = useState('all'); // 'all' or 'incorrect'
    
    // Data states
    const [quizHistory, setQuizHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Modal states
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const [showAttemptDetail, setShowAttemptDetail] = useState(false);
    const [showFullReview, setShowFullReview] = useState(false);
    const [attemptDetails, setAttemptDetails] = useState(null);
    const [questionFilter, setQuestionFilter] = useState('all'); // 'all' or 'incorrect'
    //console.log('🔍 DEBUG: questionFilter state initialized:', questionFilter);

    // Load subjects on component mount
    useEffect(() => {
        loadSubjects();
    }, [studentId]);

    // Load dependent dropdowns
    useEffect(() => {
        if (selectedSubject) {
            loadChapters(selectedSubject);
            setSelectedChapter('');
            setSelectedTopic('');
            setSelectedSubtopic('');
        }
    }, [selectedSubject]);

    useEffect(() => {
        if (selectedChapter) {
            loadTopics(selectedChapter);
            setSelectedTopic('');
            setSelectedSubtopic('');
        }
    }, [selectedChapter]);

    useEffect(() => {
        if (selectedTopic) {
            loadSubtopics(selectedTopic);
            setSelectedSubtopic('');
        }
    }, [selectedTopic]);

    // Load quiz history when filters change
    useEffect(() => {
        if (specificQuizId) {
            // If specific quiz provided, load immediately without filters
            loadQuizHistory();
        } else if (selectedSubject) {
            // Normal flow: load when subject selected
            loadQuizHistory();
        }
    }, [selectedSubject, selectedChapter, selectedTopic, selectedSubtopic, viewType, specificQuizId]);

    const loadSubjects = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/subjects/?student_id=${studentId}`);
            const data = await response.json();
            setSubjects(data || []);
        } catch (err) {
            console.error('Error loading subjects:', err);
        }
    };

    const loadChapters = async (subjectId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/chapters/subject/${subjectId}?student_id=${studentId}`);
            const data = await response.json();
            setChapters(data || []);
        } catch (err) {
            console.error('Error loading chapters:', err);
            setChapters([]);
        }
    };

    const loadTopics = async (chapterId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/topics/chapter/${chapterId}?student_id=${studentId}`);
            const data = await response.json();
            setTopics(data || []);
        } catch (err) {
            console.error('Error loading topics:', err);
            setTopics([]);
        }
    };

    const loadSubtopics = async (topicId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/subtopics/topic/${topicId}?student_id=${studentId}`);
            const data = await response.json();
            setSubtopics(data || []);
        } catch (err) {
            console.error('Error loading subtopics:', err);
            setSubtopics([]);
        }
    };

    const loadQuizHistory = async () => {
        setLoading(true);
        setError(null);

        try {
            // If specific quiz ID is provided, use different endpoint
            if (specificQuizId) {
                const response = await authFetch(`${API_BASE_URL}/quiz/report/student/${studentId}/quiz/${specificQuizId}/history`);
                
                if (!response.ok) {
                    throw new Error('Failed to load quiz history');
                }

                const data = await response.json();
                // Wrap in array since it's a single quiz
                setQuizHistory(data ? [data] : []);
                return;
            }

            // Build query parameters for regular history
            const params = new URLSearchParams({
                student_id: studentId,
                subject_id: selectedSubject
            });

            if (selectedChapter) params.append('chapter_id', selectedChapter);
            if (selectedTopic) params.append('topic_id', selectedTopic);
            if (selectedSubtopic) params.append('subtopic_id', selectedSubtopic);

            const response = await authFetch(`${API_BASE_URL}/quiz/report/student/${studentId}/detailed-history?${params}`);

            
            if (!response.ok) {
                throw new Error('Failed to load quiz history');
            }

            const data = await response.json();
            setQuizHistory(data || []);
        } catch (err) {
            setError('Failed to load quiz history. Please try again.');
            console.error('Error loading quiz history:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadAttemptDetails = async (attemptId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/attempt/${attemptId}/details?student_id=${studentId}`);
            const data = await response.json();
            setAttemptDetails(data);
            setShowAttemptDetail(true);
        } catch (err) {
            console.error('Error loading attempt details:', err);
        }
    };

    const loadFullReview = async (attemptId) => {
        try {
            const response = await authFetch(`${API_BASE_URL}/quiz/report/attempt/${attemptId}/full-review?student_id=${studentId}`);
            const data = await response.json();
            setAttemptDetails(data);
            setQuestionFilter(viewType); // Use the top-level filter selection
            setShowFullReview(true);
        } catch (err) {
            console.error('Error loading full review:', err);
        }
    };

    const getPerformanceColor = (score) => {
        if (score >= 80) return 'excellent';
        if (score >= 60) return 'good';
        if (score >= 40) return 'fair';
        return 'poor';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never attempted';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (minutes) => {
        if (!minutes || minutes === 0) return '0m';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    return (
        <div className="enhanced-quiz-history">
            {!specificQuizId && (
                <>
                    <div className="history-header">
                        <h3>Quiz History & Analysis</h3>
                        <p>Track your progress and review individual quiz attempts</p>
                    </div>

                    {/* Filter Controls */}
                    <div className="history-filters">
                        <div className="filter-row">
                            <div className="filter-group">
                                <label htmlFor="subject">Subject *</label>
                                <CustomSelect
                                    value={selectedSubject}
                                    onChange={(e) => setSelectedSubject(e.target.value)}
                                    className="filter-select"
                                    placeholder="Select Subject"
                                    options={subjects.map(s => ({ value: s.subject_id, label: s.name }))}
                                />
                            </div>
                            
                    <div className="filter-group">
                        <label htmlFor="chapter">Chapter</label>
                        <CustomSelect
                            value={selectedChapter}
                            onChange={(e) => setSelectedChapter(e.target.value)}
                            className="filter-select"
                            disabled={!selectedSubject}
                            placeholder="All Chapters"
                            options={[
                                { value: '', label: 'All Chapters' },
                                ...chapters.map(c => ({ value: c.chapter_id, label: c.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="topic">Topic</label>
                        <CustomSelect
                            value={selectedTopic}
                            onChange={(e) => setSelectedTopic(e.target.value)}
                            className="filter-select"
                            disabled={!selectedChapter}
                            placeholder="All Topics"
                            options={[
                                { value: '', label: 'All Topics' },
                                ...topics.map(t => ({ value: t.topic_id, label: t.name }))
                            ]}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="subtopic">Subtopic</label>
                        <CustomSelect
                            value={selectedSubtopic}
                            onChange={(e) => setSelectedSubtopic(e.target.value)}
                            className="filter-select"
                            disabled={!selectedTopic}
                            placeholder="All Subtopics"
                            options={[
                                { value: '', label: 'All Subtopics' },
                                ...subtopics.map(s => ({ value: s.subtopic_id, label: s.name }))
                            ]}
                        />
                    </div>
                    <div className="filter-group">
                        <label htmlFor="viewType">View Type</label>
                        <CustomSelect
                            value={viewType}
                            onChange={(e) => setViewType(e.target.value)}
                            className="filter-select"
                            options={[
                                { value: 'all', label: 'All Questions' },
                                { value: 'incorrect', label: 'Only Incorrect & Unanswered' },
                            ]}
                        />
                    </div>        
                
                
                
                </div>
            </div>
            </>
                    )}

            {/* Content Area */}
            {loading && (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    Loading quiz history...
                </div>
            )}

            {error && (
                <div className="error-message">
                    {error}
                    <button onClick={loadQuizHistory} className="retry-btn">
                        Retry
                    </button>
                </div>
            )}

            {!loading && !error && !selectedSubject && !specificQuizId && (
                <div className="select-subject-message">
                    <div className="message-icon">🎯</div>
                    <h4>Select a Subject to View Quiz History</h4>
                    <p>Choose a subject from the dropdown above to see your quiz attempts and progress.</p>
                </div>
            )}

            {!loading && !error && selectedSubject && quizHistory.length === 0 && (
                <div className="no-history-message">
                    <div className="message-icon">📝</div>
                    <h4>No Quiz History Found</h4>
                    <p>You haven't attempted any quizzes for the selected filters yet.</p>
                </div>
            )}

            {!loading && !error && quizHistory.length > 0 && (
                <div className="quiz-cards-grid">
                    {quizHistory.map(quiz => (
                        <div key={quiz.quiz_id} className="quiz-card">
                            <div className="quiz-card-header">
                                <h4 className="quiz-title">{quiz.quiz_title}</h4>
                                <div className="quiz-meta">
                                    <span className="quiz-topic">{quiz.topic_name}</span>
                                    {quiz.subtopic_name && (
                                        <span className="quiz-subtopic"> • {quiz.subtopic_name}</span>
                                    )}
                                </div>
                            </div>

                            <div className="quiz-stats">
                                <div className="stat-item">
                                    <span className="stat-label">Best Score:</span>
                                    <span className={`stat-value ${getPerformanceColor(quiz.best_score)}`}>
                                        {quiz.best_score.toFixed(1)}%
                                    </span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Attempts:</span>
                                    <span className="stat-value">{quiz.total_attempts}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Last Attempt:</span>
                                    <span className="stat-value">{formatDate(quiz.last_attempt_date)}</span>
                                </div>
                            </div>

                            <div className="attempt-badges">
                                <span className="badges-label">Attempts:</span>
                                <div className="badges-container">
                                    {quiz.attempts.map((attempt, index) => (
                                        <button
                                            key={attempt.attempt_id}
                                            className={`attempt-badge ${getPerformanceColor(attempt.score)}`}
                                            onClick={() => {
                                                setSelectedQuiz(quiz);
                                                setSelectedAttempt(attempt);
                                                loadAttemptDetails(attempt.attempt_id);
                                            }}
                                            title={`Attempt ${index + 1}: ${attempt.score}% on ${formatDate(attempt.attempt_date)}`}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="progress-trend">
                                <span className="trend-label">Progress:</span>
                                <div className="trend-line">
                                    {quiz.attempts.map((attempt, index) => (
                                        <React.Fragment key={attempt.attempt_id}>
                                            <span className={`trend-score ${getPerformanceColor(attempt.score)}`}>
                                                {attempt.score.toFixed(1)}%
                                            </span>
                                            {index < quiz.attempts.length - 1 && (
                                                <span className="trend-arrow">→</span>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>

                            <div className="quiz-actions">
                                <button 
                                    className="action-btn retake-btn"
                                    onClick={() => onQuizSelect(quiz.quiz_id)}
                                >
                                    🔄 Retake Quiz
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Attempt Detail Modal */}
            {showAttemptDetail && attemptDetails && (
                <div className="modal-overlay" onClick={() => setShowAttemptDetail(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Attempt #{selectedAttempt?.attempt_number} Summary</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowAttemptDetail(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="attempt-summary">
                                <div className="summary-stats">
                                    <div className="summary-stat">
                                        <span className="summary-label">Score:</span>
                                        <span className={`summary-value ${getPerformanceColor(attemptDetails.score)}`}>
                                            {attemptDetails.score}% ({attemptDetails.correct_answers}/{attemptDetails.total_questions})
                                        </span>
                                    </div>
                                    <div className="summary-stat">
                                        <span className="summary-label">Time Taken:</span>
                                        <span className="summary-value">{formatTime(attemptDetails.time_taken)}</span>
                                    </div>
                                    <div className="summary-stat">
                                        <span className="summary-label">Date:</span>
                                        <span className="summary-value">{formatDate(attemptDetails.attempt_date)}</span>
                                    </div>
                                </div>

                                <div className="question-analysis">
                                    <h4>Question Analysis</h4>
                                    <div className="analysis-grid">
                                        <div className="analysis-item correct">
                                            <span className="analysis-icon">✅</span>
                                            <span className="analysis-count">{attemptDetails.correct_answers}</span>
                                            <span className="analysis-label">Correct</span>
                                        </div>
                                        <div className="analysis-item incorrect">
                                            <span className="analysis-icon">❌</span>
                                            <span className="analysis-count">{attemptDetails.incorrect_answers}</span>
                                            <span className="analysis-label">Wrong</span>
                                        </div>
                                        <div className="analysis-item unanswered">
                                            <span className="analysis-icon">⏰</span>
                                            <span className="analysis-count">{attemptDetails.unanswered}</span>
                                            <span className="analysis-label">Unanswered</span>
                                        </div>
                                    </div>
                                </div>

                                {attemptDetails.weak_areas && attemptDetails.weak_areas.length > 0 && (
                                    <div className="weak-areas">
                                        <h4>Areas for Improvement</h4>
                                        <ul>
                                            {attemptDetails.weak_areas.map((area, index) => (
                                                <li key={index}>{area}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="modal-actions">
                                <button 
                                    className="action-btn primary"
                                    onClick={() => {
                                        setShowAttemptDetail(false);
                                        loadFullReview(selectedAttempt.attempt_id);
                                    }}
                                >
                                    📝 Review Wrong Answers
                                </button>
                                <button 
                                    className="action-btn secondary"
                                    onClick={() => {
                                        setShowAttemptDetail(false);
                                        setShowFullReview(false);
                                        onQuizSelect(selectedQuiz.quiz_id);
                                    }}    
                                >
                                    🔄 Retake Quiz
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Full Review Modal */}
            {showFullReview && attemptDetails && (
                console.log('🔍 DEBUG: showFullReview opened, questionFilter =', questionFilter),
                <div className="modal-overlay" onClick={() => setShowFullReview(false)}>
                    <div className="modal-content full-review" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                {console.log('🔍 DEBUG: In modal-header, questionFilter =', questionFilter)}
                                <h3>Detailed Review - Attempt #{selectedAttempt?.attempt_number}</h3>
                                <div style={{ marginTop: '10px' }}>
                                    <label htmlFor="question-filter" style={{ marginRight: '10px', fontWeight: '500', fontSize: '0.95em' }}>
                                        Show:
                                    </label>
                                    <CustomSelect
                                        value={questionFilter}
                                        onChange={(e) => setQuestionFilter(e.target.value)}
                                        options={[
                                            { value: 'all', label: 'All Questions' },
                                            { value: 'incorrect', label: 'Only Incorrect & Unanswered' },
                                        ]}
                                    />
                                </div>
                            </div>
                            <button 
                                className="modal-close"
                                onClick={() => setShowFullReview(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="review-content">
                                {attemptDetails.questions && attemptDetails.questions
                                    .filter(question => {
                                        if (questionFilter === 'all') return true;
                                        // Show only incorrect or unanswered questions
                                        return !question.is_correct || question.selected_answer === null;
                                    })
                                    .map((question, index) => (
                                    <div key={question.question_id} className={`question-review ${question.is_correct ? 'correct' : 'incorrect'}`}>
                                        <div className="question-header">
                                            <span className="question-number">Q{index + 1}</span>
                                            <span className={`question-status ${question.is_correct ? 'correct' : 'incorrect'}`}>
                                                {question.is_correct ? '✅ Correct' : '❌ Incorrect'}
                                            </span>
                                            {question.time_spent && (
                                                <span className="question-time">⏱️ {question.time_spent}s</span>
                                            )}
                                        </div>

                                        <div className="question-text">
                                            {question.question_text}
                                        </div>

                                        <div className="question-options">
                                            {['A', 'B', 'C', 'D'].map(option => {
                                                const optionText = question[`option_${option}`];
                                                if (!optionText) return null;

                                                const isSelected = question.selected_answer === option;
                                                const isCorrect = question.correct_answer === option;

                                                return (
                                                    <div 
                                                        key={option}
                                                        className={`option ${isSelected ? 'selected' : ''} ${isCorrect ? 'correct' : ''}`}
                                                    >
                                                        <span className="option-letter">{option}.</span>
                                                        <span className="option-text">{optionText}</span>
                                                        {isSelected && <span className="option-indicator">← Your answer</span>}
                                                        {isCorrect && <span className="option-indicator correct">← Correct answer</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {question.explanation && (
                                            <div className="question-explanation">
                                                <h5>Explanation:</h5>
                                                <p>{question.explanation}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* ADD THE RETAKE BUTTON SECTION HERE */}
                            <div className="modal-actions" style={{
                                marginTop: '30px', 
                                paddingTop: '20px', 
                                borderTop: '1px solid #eee',
                                textAlign: 'center'
                            }}>
                                <button 
                                    className="action-btn secondary"
                                    onClick={() => {
                                        setShowFullReview(false);
                                        onQuizSelect(selectedQuiz.quiz_id);
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        fontSize: '1rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    🔄 Retake Quiz
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedQuizHistory;